'use client';

import { useAuth } from '@/components/provider/AuthProvider';
import { UserAvatar } from '@/components/UserAvatar';
import Container from './ui/container';
import { Button, buttonVariants } from './ui/button';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { LanguageNav } from './LanguageNav';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer';
import {
	RectangleStackIcon,
	LinkIcon,
	QrCodeIcon,
	ChartBarIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { QrcodlyLogo } from './QrcodlyLogo';
import { generateRandomString, generateCodeChallenge } from '@/lib/pkce';

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.1,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, x: -20 },
	visible: {
		opacity: 1,
		x: 0,
		transition: {
			duration: 0.3,
		},
	},
};

export default function Header({
	hideDashboardLink = false,
	hideLogo = false,
	hideLanguageNav = false,
}) {
	const t = useTranslations('header');
	const pathname = usePathname();
	const { isSignedIn } = useAuth();

	const oidcIssuer = process.env.NEXT_PUBLIC_OIDC_ISSUER_URL || 'https://keycloak.dslan.at/realms/dslan';
	const oidcClientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID || 'qrcodly';
	const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || 'https://qr.dslan.at';
	
	const handleSignIn = async (e?: React.MouseEvent) => {
		if (e) e.preventDefault();
		const verifier = generateRandomString(64);
		const challenge = await generateCodeChallenge(verifier);
		document.cookie = `qrcodly_pkce_verifier=${verifier}; path=/; max-age=600; SameSite=Lax`;
		const target = `${oidcIssuer}/protocol/openid-connect/auth?client_id=${oidcClientId}&redirect_uri=${encodeURIComponent(`${frontendUrl}/api/auth/callback/oidc`)}&response_type=code&scope=openid%20profile%20email&code_challenge=${challenge}&code_challenge_method=S256`;
		window.location.href = target;
	};

	const signInHref = `${oidcIssuer}/protocol/openid-connect/auth?client_id=${oidcClientId}&redirect_uri=${encodeURIComponent(`${frontendUrl}/api/auth/callback/oidc`)}&response_type=code&scope=openid%20profile%20email`;
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [productsOpen, setProductsOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 20);
		handleScroll();
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const isFeaturesActive = pathname === '/features';
	const isProductsActive = pathname.startsWith('/products');

	const openProducts = useCallback(() => {
		if (closeTimeout.current) {
			clearTimeout(closeTimeout.current);
			closeTimeout.current = null;
		}
		setProductsOpen(true);
	}, []);

	const closeProducts = useCallback(() => {
		closeTimeout.current = setTimeout(() => setProductsOpen(false), 200);
	}, []);

	// Close dropdown and mobile drawer on route change
	useEffect(() => {
		setProductsOpen(false);
		setMobileMenuOpen(false);
	}, [pathname]);

	// Close dropdown on click outside
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setProductsOpen(false);
			}
		}
		if (productsOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
		return undefined;
	}, [productsOpen]);

	const productLinks = [
		{
			href: '/products/url-shortener' as const,
			title: t('urlShortenerLink'),
			description: t('urlShortenerDesc'),
			icon: <LinkIcon className="h-5 w-5" />,
		},
		{
			href: '/products/qr-codes' as const,
			title: t('qrCodesLink'),
			description: t('qrCodesDesc'),
			icon: <QrCodeIcon className="h-5 w-5" />,
		},
		{
			href: '/products/analytics' as const,
			title: t('analyticsLink'),
			description: t('analyticsDesc'),
			icon: <ChartBarIcon className="h-5 w-5" />,
		},
	];

	return (
		<header
			className={cn(
				'sticky top-0 z-[100] w-full transition-all duration-200',
				scrolled
					? 'bg-white/80 backdrop-blur-md border-b border-slate-200/80 shadow-xs'
					: 'bg-transparent border-b border-transparent',
			)}
		>
			<Container disableOverflow>
				<div className="flex h-16 sm:h-20 items-center justify-between gap-4">
					<div className="flex items-center gap-6">
						{!hideLogo && (
							<div className="text-black">
								<Link title="QRcodly" href="/">
									<QrcodlyLogo size="default" />
								</Link>
							</div>
						)}
					</div>
					<div className="flex items-center gap-3 font-medium text-slate-700">
						{/* Products dropdown */}
						<div
							ref={dropdownRef}
							className="relative hidden lg:block"
							onMouseEnter={openProducts}
							onMouseLeave={closeProducts}
						>
							<button
								type="button"
								className={cn(
									'flex items-center gap-1.5 h-10 px-3 text-slate-700 hover:text-black transition-colors rounded-lg hover:bg-slate-100/80 font-medium text-sm',
									(isProductsActive || productsOpen) && 'font-semibold text-black bg-slate-100/80',
								)}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setProductsOpen((prev) => !prev);
								}}
							>
								{t('productsBtn')}
								<ChevronDownIcon
									className={cn(
										'h-4 w-4 text-slate-400 transition-transform duration-200',
										productsOpen && 'rotate-180 text-black',
									)}
								/>
							</button>

							<AnimatePresence>
								{productsOpen && (
									<motion.div
										initial={{ opacity: 0, y: 8, scale: 0.98 }}
										animate={{ opacity: 1, y: 0, scale: 1 }}
										exit={{ opacity: 0, y: 6, scale: 0.98 }}
										transition={{ duration: 0.15, ease: 'easeOut' }}
										className="absolute left-0 top-full pt-2 w-80 z-[110]"
										onMouseEnter={openProducts}
										onMouseLeave={closeProducts}
									>
										<div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-2.5 backdrop-blur-xl">
											<div className="space-y-1">
												{productLinks.map((link) => {
													const isActive = pathname.startsWith(link.href);
													return (
														<Link
															key={link.href}
															href={link.href}
															className={cn(
																'flex items-start gap-3 p-2.5 rounded-xl transition-all duration-150 group',
																isActive ? 'bg-slate-100' : 'hover:bg-slate-50',
															)}
														>
															<div
																className={cn(
																	'w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0 text-slate-500 group-hover:bg-gradient-to-br group-hover:from-slate-900 group-hover:via-slate-800 group-hover:to-slate-900 group-hover:text-white transition-colors',
																	isActive &&
																		'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white',
																)}
															>
																{link.icon}
															</div>
															<div>
																<div className="text-sm font-semibold text-slate-900">
																	{link.title}
																</div>
																<div className="text-xs text-slate-500 leading-snug">
																	{link.description}
																</div>
															</div>
														</Link>
													);
												})}
											</div>
										</div>
									</motion.div>
								)}
							</AnimatePresence>
						</div>
						<Link
							href="/features"
							className={cn(
								'hidden lg:block h-10 px-3 py-2 text-sm',
								isFeaturesActive && 'font-semibold text-black',
							)}
						>
							{t('featuresBtn')}
						</Link>
						{isSignedIn ? (
							<div className="flex items-center gap-3">
								{!hideDashboardLink && (
									<div className="flex items-center gap-2">
										<Link
											href="/dashboard/qr-codes"
											aria-label={t('collectionBtn')}
											className={cn(
												buttonVariants({ size: 'icon' }),
												'hidden sm:inline-flex lg:hidden',
											)}
										>
											<RectangleStackIcon className="h-6 w-6 text-white" />
										</Link>
										<Link
											href="/dashboard/qr-codes"
											className={cn(buttonVariants(), 'hidden lg:inline-flex')}
										>
											{t('collectionBtn')}
										</Link>
									</div>
								)}
								<Link href="/dashboard/settings/profile">
									<UserAvatar />
								</Link>
							</div>
						) : (
							<Button onClick={handleSignIn}>{t('signInBtn')}</Button>
						)}
						{!hideLanguageNav && (
							<div className="hidden lg:block">
								<LanguageNav />
							</div>
						)}
						{/* Mobile menu button */}
						<div
							className="flex items-center justify-center lg:hidden xs:p-2 cursor-pointer"
							onClick={() => setMobileMenuOpen(true)}
						>
							<Bars3Icon className="h-8 w-8 text-black" />
						</div>
					</div>
				</div>
			</Container>

			<Drawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} direction="right">
				<DrawerContent className="pt-20 pb-8 px-1 overflow-y-auto">
					<DrawerHeader className="hidden">
						<DrawerTitle>Navigation</DrawerTitle>
					</DrawerHeader>
					<div className="absolute top-6 left-8 right-4 flex items-center justify-between">
						<div className="text-black">
							<Link title="QRcodly" href="/">
								<QrcodlyLogo size="default" />
							</Link>
						</div>
						<DrawerClose asChild>
							<Button size="icon" variant={'ghost'}>
								<XMarkIcon className="h-6 w-6" />
							</Button>
						</DrawerClose>
					</div>

					<motion.div
						className="space-y-2 flex-1 px-3"
						variants={containerVariants}
						initial="hidden"
						animate={mobileMenuOpen ? 'visible' : 'hidden'}
					>
						<motion.div variants={itemVariants}>
							<div className="px-4 pt-2 pb-1 text-xs font-semibold text-slate-900 uppercase tracking-wider">
								{t('productsBtn')}
							</div>
						</motion.div>
						{productLinks.map((link) => (
							<motion.div key={link.href} variants={itemVariants}>
								<Link
									href={link.href}
									className={buttonVariants({
										variant: 'ghost',
										className: cn(
											'w-full justify-start text-foreground font-semibold',
											pathname === link.href && 'bg-accent',
										),
									})}
								>
									<span className="mr-2 text-slate-400">{link.icon}</span>
									{link.title}
								</Link>
							</motion.div>
						))}
						<motion.div variants={itemVariants}>
							<Link
								href="/features"
								className={buttonVariants({
									variant: 'ghost',
									className: cn(
										'w-full justify-start text-foreground font-semibold',
										isFeaturesActive && 'bg-accent',
									),
								})}
							>
								{t('featuresBtn')}
							</Link>
						</motion.div>
						{isSignedIn ? (
							<motion.div variants={itemVariants}>
								<Link
									href="/dashboard/qr-codes"
									className={buttonVariants({
										className: 'justify-start text-foreground font-semibold',
									})}
								>
									{t('collectionBtn')}
								</Link>
							</motion.div>
						) : (
							<motion.div variants={itemVariants}>
								<Link
									href={signInHref}
									className={buttonVariants({
										className: 'w-full justify-start text-foreground font-semibold',
									})}
								>
									{t('signInBtn')}
								</Link>
							</motion.div>
						)}
						{!hideLanguageNav && (
							<motion.div variants={itemVariants}>
								<div className="border-t border-slate-100 mt-2 pt-4">
									<LanguageNav variant="dropdown-up" />
								</div>
							</motion.div>
						)}
					</motion.div>
				</DrawerContent>
			</Drawer>
		</header>
	);
}
